import * as React from 'react'
import { useNavigate } from 'react-router'
import { type ToastProps } from '@components'
import baseApi from '@core/api'
import store from '@core/store'
import i18n from '@core/translations'
import type { PaymentMinimal } from '@payment/api'
import { useNotification } from '@core/hooks'
import { SendPostMessage, useTheme } from '@core/utils'
import { POST_MESSAGE_EVENT } from '@core/constants'

const dispatch = store.dispatch

const usePaymentEvents = () => {
    const theme = useTheme()

    const navigate = useNavigate()
    const { toast, alert } = useNotification()

    const complete = React.useCallback(() => {
        SendPostMessage(
            JSON.stringify({
                success: true,
                event: POST_MESSAGE_EVENT.PAYMENT.COMPLETE,
            }),
            POST_MESSAGE_EVENT.PAYMENT.COMPLETE,
        )

        setTimeout(
            () =>
                alert({
                    title: i18n.t('@payment.utils.PaymentAlert.alert_title'),
                    message: i18n.t(
                        '@payment.utils.PaymentAlert.alert_message',
                    ),
                    iconColor: theme.colors.semantic.content.success,
                    icon: 'check-circle',
                    duration: 10_000,
                    actions: [
                        {
                            children: i18n.t(
                                'context.TopUpProvider.alert_success_action',
                            ),
                        },
                    ],
                }),
            750,
        )
    }, [alert, theme.colors.semantic.content.success])

    const refund = React.useCallback(() => {
        SendPostMessage(
            JSON.stringify({
                success: false,
                event: POST_MESSAGE_EVENT.PAYMENT.CANCEL_OPERATOR,
            }),
            POST_MESSAGE_EVENT.PAYMENT.CANCEL_OPERATOR,
        )

        setTimeout(
            () =>
                alert({
                    title: i18n.t(
                        '@payment.utils.RefundPaymentAlert.alert_title',
                    ),
                    message: i18n.t(
                        '@payment.utils.RefundPaymentAlert.alert_message',
                    ),
                    iconColor: theme.colors.semantic.content.info,
                    icon: 'warning',
                    duration: 10_000,
                    actions: [
                        {
                            children: i18n.t(
                                'context.TopUpProvider.alert_success_action',
                            ),
                        },
                    ],
                }),
            750,
        )
    }, [alert, theme.colors.semantic.content.info])

    const cancel = React.useCallback(() => {
        SendPostMessage(
            JSON.stringify({
                success: false,
                event: POST_MESSAGE_EVENT.PAYMENT.CANCEL_OPERATOR,
            }),
            POST_MESSAGE_EVENT.PAYMENT.CANCEL_OPERATOR,
        )

        setTimeout(
            () =>
                alert({
                    title: i18n.t(
                        '@payment.utils.CancelPaymentAlert.alert_title',
                    ),
                    message: i18n.t(
                        '@payment.utils.CancelPaymentAlert.alert_message',
                    ),
                    iconColor: theme.colors.semantic.content.error,
                    icon: 'close-circle',
                    duration: 10_000,
                    actions: [
                        {
                            children: i18n.t(
                                'context.TopUpProvider.alert_success_action',
                            ),
                        },
                    ],
                }),
            750,
        )
    }, [alert, theme.colors.semantic.content.error])

    const redirect = React.useCallback(
        (walletId: number) =>
            navigate('/', { replace: true, state: { walletId } }),
        [navigate],
    )

    return React.useCallback(
        async (event: PaymentMinimal) => {
            const toastParams: Omit<ToastProps, 'open'> = {
                variant: 'error',
                icon: 'warning',
                message: i18n.t(
                    'socket_context.payment_event.toast_error_message',
                ),
                duration: 5000,
            }

            if (event.status === 'no_balance') {
                toastParams.message = i18n.t(
                    'socket_context.payment_event.toast_no_balance_message',
                )
            }

            if (event.status === 'expire') {
                toastParams.message = i18n.t(
                    'socket_context.payment_event.toast_expire_message',
                )
            }

            if (event.status === 'device_offline') {
                toastParams.message = i18n.t(
                    'socket_context.payment_event.toast_device_offline_message',
                )
            }

            if (event.status === 'callback_waiting') {
                return
            }

            if (event.status === 'callback_no') {
                toastParams.message = i18n.t(
                    'socket_context.payment_event.toast_callback_no',
                )
            }

            if (event.status === 'service_error') {
                toastParams.message = ''
            }

            if (event.status === 'discount_point_change') {
                toastParams.message = i18n.t(
                    'socket_context.payment_event.toast_discount_point_change',
                )

                redirect(event.original_wallet_id ?? event.wallet_id)
            }

            if (event.status === 'approval') {
                return
            }

            if (event.status === 'approved') {
                return
            }

            if (event.status === 'pre_complete') {
                return
            }

            if (event.status === 'complete') {
                dispatch(baseApi.util.invalidateTags(['WalletDetail']))

                redirect(event.original_wallet_id ?? event.wallet_id)

                if (event.refund_amount > 0) return refund()

                return complete()
            }

            if (event.status === 'cancel') {
                redirect(event.original_wallet_id ?? event.wallet_id)

                return cancel()
            }

            toast(toastParams)
        },
        [toast, redirect, refund, complete, cancel],
    )
}

export default usePaymentEvents
